import { Box, Flex, Icon, Text } from "@chakra-ui/react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { Link as RouterLink } from "@tanstack/react-router"
import {
  FiBook,
  FiBookOpen,
  FiBookmark,
  FiClipboard,
  FiCreditCard,
  FiList,
  FiMessageCircle,
  FiPlayCircle,
  FiSearch,
  FiSettings,
  FiTag,
  FiUsers,
  FiZap,
} from "react-icons/fi"
import {
  RiDashboardLine,
  RiGraduationCapLine,
  RiOrganizationChart,
  RiTestTubeLine,
} from "react-icons/ri"

import { OrganizationsService, type UserPublic } from "@/client"
import { coerceFeedbackEnabled } from "@/hooks/useFeedbackEnabled"
import { coerceFlashcardsEnabled } from "@/hooks/useFlashcardsEnabled"
import { useStudentLabels } from "@/hooks/useStudentLabels"
import { getCurrentOrgId } from "@/utils"

const baseItems = [
  { icon: RiDashboardLine, title: "Dashboard", path: "/" },
  // { icon: FiBriefcase, title: "Items", path: "/items" },
]

const settingsItems = [
  { icon: FiSettings, title: "User Settings", path: "/settings" },
]

// Search is for everyone: students and teachers/admins alike.
const searchItem = {
  icon: FiSearch,
  title: "Search",
  path: "/search",
}

const studentQuizItem = {
  icon: FiPlayCircle,
  title: "Take Quiz",
  path: "/quizzes",
}

const practiceQuizItem = {
  icon: RiTestTubeLine,
  title: "Practice Quizzes",
  path: "/practice-quizzes",
}

const myAttemptsItem = {
  icon: FiList,
  title: "My Attempts",
  path: "/attempts-history",
}

const savedQuestionsItem = {
  icon: FiBookmark,
  title: "Bookmarks",
  path: "/saved-questions",
}

const studyItem = {
  icon: FiZap,
  title: "Flashcards",
  path: "/study",
}

const myFeedbackItem = {
  icon: FiMessageCircle,
  title: "My Feedback",
  path: "/my-feedback",
}

const teacherQuestionsItem = {
  icon: FiBookOpen,
  title: "Questions",
  path: "/questions",
}

const teacherQuizManagementItem = {
  icon: FiClipboard,
  title: "Manage Quizzes",
  path: "/quiz-management",
}

const flashcardsItem = {
  icon: FiCreditCard,
  title: "Flashcards",
  path: "/flashcards",
}

const feedbackManagementItem = {
  icon: FiMessageCircle,
  title: "Manage Feedback",
  path: "/feedback",
}

const programsItem = {
  icon: RiGraduationCapLine,
  title: "Programs",
  path: "/programs",
}

const orgAdminItems = [{ icon: FiBook, title: "Subjects", path: "/subjects" }]
const adminTopicManagementItem = {
  icon: FiTag,
  title: "Topics",
  path: "/admin-topics",
}

interface SidebarItemsProps {
  onClose?: () => void
}

const SidebarItems = ({ onClose }: SidebarItemsProps) => {
  const queryClient = useQueryClient()
  const currentUser = queryClient.getQueryData<UserPublic>(["currentUser"])
  const currentOrgId = getCurrentOrgId()

  // Fetch organizations to check if user has admin access
  const { data: organizations } = useQuery({
    queryKey: ["organizations", currentUser?.id],
    queryFn: () => OrganizationsService.listOrganizations(),
    enabled: !!currentUser && !currentUser.is_superuser, // Only fetch if not superuser
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  })

  // Fetch user's memberships to check for teacher/admin roles
  const { data: memberships } = useQuery({
    queryKey: ["myMemberships", currentUser?.id],
    queryFn: () => OrganizationsService.getMyMemberships(),
    enabled: !!currentUser && !currentUser.is_superuser, // Only fetch if not superuser
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  })

  const { data: currentOrganization } = useQuery({
    queryKey: ["currentOrganization", currentOrgId],
    queryFn: () =>
      OrganizationsService.getOrganization({ orgId: currentOrgId as string }),
    enabled: Boolean(currentOrgId),
    staleTime: 5 * 60 * 1000,
  })
  const practiceLimit = (() => {
    if (!currentOrganization?.settings) {
      return null
    }
    const settings = currentOrganization.settings as {
      practice_test_limit?: unknown
    }
    return typeof settings.practice_test_limit === "number"
      ? settings.practice_test_limit
      : null
  })()

  const feedbackEnabled = coerceFeedbackEnabled(currentOrganization?.settings)
  const flashcardsEnabled = coerceFlashcardsEnabled(
    currentOrganization?.settings,
  )
  const {
    dashboardLabel,
    quizzesLabel,
    practiceQuizzesLabel,
    attemptsLabel,
    feedbackLabel,
    flashcardsLabel,
    settingsLabel,
  } = useStudentLabels()

  // Determine if user can access organizations page
  const canAccessOrganizations =
    currentUser?.is_superuser ||
    (organizations?.data && organizations.data.length > 0)

  const hasTeacherOrAdminRole = Boolean(
    currentUser?.is_superuser ||
      memberships?.some((m) => m.role === "teacher" || m.role === "admin"),
  )

  const isStudentOnly = Boolean(currentUser && !hasTeacherOrAdminRole)

  // Determine if user can access questions (teacher or admin in any org)
  const canAccessQuestions = hasTeacherOrAdminRole

  // Determine if user can manage quizzes (teacher or admin in any org)
  const canManageQuizzes = hasTeacherOrAdminRole

  // Determine if user can access subjects (admin only in any org)
  const canAccessSubjects =
    currentUser?.is_superuser || memberships?.some((m) => m.role === "admin")

  // Build the items list in correct order
  const items = [{ ...baseItems[0], title: dashboardLabel }]

  // Search sits directly under the dashboard for every role
  items.push(searchItem)

  // Add student quiz entry for members without elevated permissions
  if (isStudentOnly) {
    items.push({ ...studentQuizItem, title: quizzesLabel })
  }

  // Add practice quiz entry only for students (not teachers/admins)
  if (isStudentOnly && practiceLimit !== 0) {
    items.push({ ...practiceQuizItem, title: practiceQuizzesLabel })
  }

  // Add My Attempts entry for students
  if (isStudentOnly) {
    items.push({ ...myAttemptsItem, title: attemptsLabel })
    items.push(savedQuestionsItem)
  }

  // Add My Feedback entry for students
  if (isStudentOnly && feedbackEnabled) {
    items.push({ ...myFeedbackItem, title: feedbackLabel })
  }

  // Add Study hub for students
  if (isStudentOnly && flashcardsEnabled) {
    items.push({ ...studyItem, title: flashcardsLabel })
  }

  // Add Organizations link if user has access (after Dashboard)
  if (canAccessOrganizations) {
    items.push({
      icon: RiOrganizationChart,
      title: "Organizations",
      path: "/organizations",
    })
  }

  // Add Programs link for admins only
  if (canAccessSubjects) {
    items.push(programsItem)
  }

  // Add Subjects link for org admins only
  if (canAccessSubjects) {
    items.push(...orgAdminItems)
    items.push(adminTopicManagementItem)
  }

  // Add Questions link for teachers/admins (always before Settings)
  if (canAccessQuestions) {
    items.push(teacherQuestionsItem)
  }

  if (canManageQuizzes) {
    items.push(teacherQuizManagementItem)
  }

  // Add Flashcard deck management for teachers/admins
  if (hasTeacherOrAdminRole && flashcardsEnabled) {
    items.push(flashcardsItem)
  }

  // Add Feedback Management for teachers/admins
  if (hasTeacherOrAdminRole && feedbackEnabled) {
    items.push(feedbackManagementItem)
  }

  // Add Settings at the end
  items.push({ ...settingsItems[0], title: settingsLabel })

  // Add Admin link for superusers
  if (currentUser?.is_superuser) {
    items.push({ icon: FiUsers, title: "Admin", path: "/admin" })
  }

  const listItems = items.map(({ icon, title, path }) => (
    <RouterLink key={title} to={path} onClick={onClose}>
      <Flex
        gap={4}
        px={4}
        py={2}
        _hover={{
          background: "gray.subtle",
        }}
        alignItems="center"
        fontSize="sm"
      >
        <Icon as={icon} alignSelf="center" />
        <Text ml={2}>{title}</Text>
      </Flex>
    </RouterLink>
  ))

  return (
    <>
      <Text fontSize="xs" px={4} py={2} fontWeight="bold">
        Menu
      </Text>
      <Box>{listItems}</Box>
    </>
  )
}

export default SidebarItems
